export type FitColumn = {
  id: string;
  // The column's share of the width, against the others' (AG starts every
  // column of a board at the same width, so they share equally)
  flex: number;
  minWidth?: number;
  maxWidth?: number;
};

// AG's sizeColumnsToFit: the columns share `width` in proportion to their
// flex, each held between its min and max width. A column held at a limit
// takes that width and the rest share what's left, until none is held. The
// widths are whole pixels, the odd pixels going to the first columns, so they
// add up to `width` unless the limits stop them (too narrow: the table
// scrolls; too wide: space is left over).
const fitColumnsToWidth = ({
  columns,
  width,
}: {
  columns: FitColumn[];
  width: number;
}): { [columnId: string]: number } => {
  const widths: { [columnId: string]: number } = {};
  let remaining = columns.filter((c) => c.flex > 0);
  let available = width;

  while (remaining.length > 0) {
    const totalFlex = remaining.reduce((sum, c) => sum + c.flex, 0);
    const toShare = available;
    const shareOf = (c: FitColumn) => (toShare * c.flex) / totalFlex;
    const held = remaining.filter((c) => {
      const share = shareOf(c);
      return (
        (c.minWidth !== undefined && share < c.minWidth) ||
        (c.maxWidth !== undefined && share > c.maxWidth)
      );
    });

    if (held.length === 0) {
      const floored = remaining.map((c) => Math.floor(shareOf(c)));
      let leftOver =
        Math.round(toShare) - floored.reduce((sum, w) => sum + w, 0);
      remaining.forEach((c, index) => {
        const extra = leftOver > 0 ? 1 : 0;
        leftOver -= extra;
        widths[c.id] = floored[index] + extra;
      });
      break;
    }

    held.forEach((c) => {
      const share = shareOf(c);
      const limit =
        c.minWidth !== undefined && share < c.minWidth
          ? c.minWidth
          : (c.maxWidth as number);
      widths[c.id] = limit;
    });
    available -= held.reduce((sum, c) => sum + widths[c.id], 0);
    remaining = remaining.filter((c) => !held.includes(c));
  }

  return widths;
};

export default fitColumnsToWidth;
